import buildQuery from 'odata-query';
import store from 'store';
import axios from 'axios';

import { IDomainSetting } from 'models/DomainSetting';
import { IRequestConfig, MethodsTypes } from 'models/Axios';
import { IQuotesData, IQuotes, ResetCdsProps, IMinimumBilling } from 'models/Quote';
import { IItemObservation, INotParticipateObservation } from 'models/portalModels/ItemObservation';

import { FilterDataSelectedCustomerOData } from 'utils/SelectedCustomerId';
import Services from './index.service';

import toast from 'components/Toast/dispatch';
import { getSegmentByCode } from 'pages/Quotes/Details/utils';

export default class QuoteSevices extends Services<IQuotesData, IQuotes> {
  /**
   * Function to get a limited qtd of items
   * @currentPage Range to skip, you need only pass currentPage (ex: currentPage = 1)
   * @pageSize Range to take, you need only pass pageSize (ex: pageSize = 15)
   */

  saveQuotation = (quoteId: number, data: any) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Quotation/save-quotation/${quoteId}`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.post,
      data,
    };

    return axios(config);
  };

  saveQuoteUnsentItems = (quoteId: number, data: any) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Quotation/SaveQuoteSent/${quoteId}`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.post,
      data,
    };

    return axios(config);
  };

  saveNegotiation = (quoteId: number, data: any) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Quotation/SaveNegotiation/${quoteId}`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.post,
      data,
    };

    return axios(config);
  };

  updateFinishDate = (quoteId: number | string) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Quotation/UpdateFinishDate/${quoteId}`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.post,
    };

    return axios(config);
  };

  get = (
    currentPage?: number,
    pageSize?: number,
    orderby?: string,
    filter?: {}
  ) => {
    const { customer } = store.getState();
    const { user } = store.getState();
    const skip = currentPage || 0;
    const top = pageSize || 20;
    const order = orderby ? `${orderby}` : undefined;

    const existSelectedCustomer: Array<string> | string = user?.selectedCustomerId!;

    const filterQuery = buildQuery({
      filter: {
        ...filter,
        ...(existSelectedCustomer && FilterDataSelectedCustomerOData()),
        ...(!existSelectedCustomer && {
          customerId: customer?.id || user?.selectedCustomerId,
        })
      },
    });

    const config: IRequestConfig<IQuotes> = {
      url: `/quote${filterQuery}`,
      params: {
        $top: `${top}`,
        $skip: `${skip * top}`,
        $orderby: order,
      },
    };

    return this.httpRequest(config);
  };

  quoteDetails = (quoteId?: string) => {
    const config: IRequestConfig<IQuotes> = {
      baseUrl: this.mafraq_url,
      url: `/Quotation/quote-suggestion-header?quoteId=${quoteId}`,
    };

    return this.httpRequest(config);
  };

  quoteDetailsWithCD = (quoteId?: string) => {
    const config: IRequestConfig<IQuotes> = {
      baseUrl: this.mafraq_url,
      url: `/Quotation/QuoteSentHeader/${quoteId}`,
    };

    return this.httpRequest(config);
  };

  quoteSuggestion = (
    quoteId: string,
    currentPage?: number,
    pageSize?: number,
    orderby?: string,
    filter?: any,
    savedInMoney?: boolean
  ) => {
    const skip = currentPage || 0;
    const top = pageSize || 20;
    const order = orderby ? `${orderby}` : undefined;
    const filterQuery = filter || undefined;
    const url = savedInMoney
      ? `/Quotation/quote-suggestion-detail/${quoteId}/true`
      : `/Quotation/quote-suggestion-detail/${quoteId}`;

    const config: IRequestConfig<IQuotes> = {
      baseUrl: this.mafraq_url,
      url,
      params: {
        $top: `${top}`,
        $skip: `${skip * top}`,
        $orderby: order,
        $filter: filterQuery,
      },
    };
    return this.httpRequest(config);
  };

  quoteSuggestionDetailSegment = (
    QuoteId: string,
    segment: string,
    qtdItemsSegment: number,
    isNoSegmented: boolean,
    useMoney: boolean,
    PageNumber?: number,
    sequenceNumber: string | null = null) => {
    const url = `/Quotation/SuggestionDetailsSegment`;

    const config: IRequestConfig<IQuotes> = {
      baseUrl: this.mafraq_url,
      url,
      params: {
        QuoteId,
        Segment: getSegmentByCode(segment),
        PageNumber,
        sequenceNumber,
        qtdItemsSegment,
        isNoSegmented,
        useMoney
      },
    };

    return this.httpRequest(config);
  };

  quoteSuggestionDetailItem = async (
    QuoteId: string,
    segment: string,
    qtdItemsSegment: number,
    isNoSegmented: boolean,
    useMoney: boolean,
    PageNumber?: number,
    sequenceNumber: string | null = null) => {
    let config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Quotation/SuggestionDetailsItem`,
      method: MethodsTypes.get,
      headers: {
        Authorization: `Bearer ${this.access_token()}`,
      },
      params: {
        QuoteId,
        Segment: getSegmentByCode(segment),
        PageNumber,
        sequenceNumber,
        qtdItemsSegment,
        isNoSegmented,
        useMoney
      }
    };

    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return error.response;
      }
    );
    return axios(config).then((result) => result);
  };

  suggestionDetailsSendQuotation = (
    QuoteId: string,
    distributionCenter: string,
    useMoney: boolean,
    PageNumber?: number,
    sequenceNumber: string | null = null) => {
    const url = `/Quotation/SuggestionDetailsSendQuotation`;

    const config: IRequestConfig<IQuotes> = {
      baseUrl: this.mafraq_url,
      url,
      params: {
        QuoteId: QuoteId,
        PageNumber: PageNumber,
        sequenceNumber: sequenceNumber,
        distributionCenter,
        useMoney
      },
    };

    return this.httpRequest(config);
  };

  getFinancialCreditInformations = (crmCustomerCode: string) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Customer/financial/credit?crmCustomerCode=${crmCustomerCode}`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      }
    };

    return axios(config);
  };

  getGroupPriceHistory = (productCode: string, crmCustomerCode: string) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Budget/group-price-history`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      params: {
        ProductCode: productCode,
        CrmCustomerCode: crmCustomerCode
      }
    };

    return axios(config);
  };

  getHistoryPriceInformations = (
    quoteId: string,
    sequenceNumber: number,
    supplierProductcode: string,
    crmCustomerCode: string

  ) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Budget/price-history`,

      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      params: {
        QuoteId: quoteId,
        SequenceNumber: sequenceNumber,
        ProductCode: supplierProductcode,
        CrmCustomerCode: crmCustomerCode
      }

    };

    return axios(config);
  };

  updateSuggestion = (idSuggestion: string) => {
    const config: IRequestConfig<IQuotes> = {
      baseUrl: this.mafraq_url,
      method: MethodsTypes.put,
      url: `/MapProduct/update-by-id`,
      params: {
        id: idSuggestion,
      },
    };
    return this.httpRequest(config);
  };

  saveBudget = (data: any) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Budget/save-budget/`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.post,
      data: data
    };

    return axios(config);
  };

  saveObsFieldText = (quoteId: string, sequenceNumberValue: any, data?: IItemObservation) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Quotation/save-item-notes/${quoteId}/${sequenceNumberValue}`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.post,
      data: `"${data?.itemObservation}"`
    };

    return axios(config);
  };

  saveObsFieldSupplierText = (quoteId: string, data?: IItemObservation) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Quotation/save-quotation-supplier-note/${quoteId}`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.post,
      data: `"${data?.itemObservation}"`
    };

    return axios(config);
  };

  saveFieldNotParticipateStatus = (data?: INotParticipateObservation) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Quotation/save-quotation-not-participate-status`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.post,
      data
    };

    return axios(config);
  };

  rollbackFieldNotParticipateStatus = (data?: INotParticipateObservation) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Quotation/rollback-quotation-not-participate-status`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.post,
      data
    };

    return axios(config);
  };

  getItemStock = (supplierProductcode: string, customerCode?: string) => {
    const url = `/Quotation/get-item-stock/${supplierProductcode}/${customerCode}`;
    const config: IRequestConfig<any> = {
      baseUrl: this.mafraq_url,
      url,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.get
    };
    return this.httpRequest(config);
  };

  quotationViewed = async (quoteId: string, quotationViewed?: boolean) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Quotation/quotation-viewed/${quoteId}`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      params: {
        statusViewedQuotation: quotationViewed,
      },
      method: MethodsTypes.post,
      data: quotationViewed
    };

    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return error.response;
      }
    );

    const request = await axios(config);

    if (request.status !== 200) {
      toast.error('Erro ao atualizar status da cotação.');
      return false;
    }

    return true;
  };

  quotationViewedIsRead = async (quoteId: string, isRead?: boolean) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Quotation/quotation-viewed-is-read/${quoteId}`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      params: {
        isRead
      },
      method: MethodsTypes.post
    };

    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return error.response;
      }
    );

    const request = await axios(config);

    if (request.status !== 200) {
      toast.error('Erro ao atualizar status da cotação.');
      return false;
    }

    return true;
  };

  checkQuotationViewed = (quoteId: string) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Quotation/check-quotation-viewed/${quoteId}`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.get,
    };

    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return error.response;
      }
    );

    return axios(config);
  };

  CheckAnalyzedQuote = (quoteId: string) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Quotation/CheckAnalyzedQuote/${quoteId}`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.post,
    };

    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return error.response;
      }
    );

    return axios(config);
  };

  resetAllBugets = (data: any) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Budget/reset-all-budgets/`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.post,
      data: data
    };

    return axios(config);
  };

  resetCDsUnsetItems = (data: ResetCdsProps) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Budget/QuoteSent/ResetBudgets`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.post,
      data: data
    };

    return axios(config);
  };

  productStatusUpdate = (data: any, distributionCenter?: string, productCode?: string, customerCode?: string) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Product/blocked-product?distributionCenter=${data.distributionCenter}
            &productCode=${data.productCode}&crmCustomerCode=${data.customerCode}`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      data,
    };

    return axios(config);
  };

  public async downloadData(filter: {}, orderby?: string) {
    const { customer } = store.getState();
    const order = orderby ?? undefined;

    if (customer) {
      filter = {
        customerId: customer?.id,
        ...filter
      };
    }

    const filterQuery = buildQuery({ filter });

    const config: IRequestConfig<IQuotes> = {
      url: `/quote/export-quote-excel${filterQuery}`,
      params: {
        $top: `99999999`,
        $skip: `0`,
        $orderby: order,
      },
      timeout: 300000
    };

    return await this.httpRequest(config)
      .then(result => {
        result.data ?
          QuoteSevices.CreateDownloadLink(result.data.fileUrl) :
          toast.error('Desculpe, erro inesperado!');
      })
      .catch(() => toast.error('Desculpe, erro inesperado!'));
  }

  public static CreateDownloadLink = (path: string | undefined) => {
    if (path) {
      const link = document.createElement('a');
      link.href = path;
      link.download = 'download';
      link.click();
    }
  };

  resetBuget = (
    data: any
  ) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Budget/reset-budget/`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.post,
      data: data
    };

    return axios(config);
  };

  getBlockedBranches = (clientId: string, quoteId: string) => {
    const config: IRequestConfig<IDomainSetting> = {
      baseUrl: this.mafraq_url,
      url: `/Budget/BlockedBranches/${clientId}/${quoteId}`,
      headers: {
        Authorization: `Bearer ${this.access_token()}`,
      },
    };
    return this.httpRequest(config);
  };

  budgetsSavedInQuote = (quotationId: string, sent: boolean) => {
    const config: IRequestConfig<string> = {
      url: `${this.mafraq_url}/Quotation/BudgetsSavedInQuote/`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.get,
      params: {
        quotationId,
        sent
      }
    };

    return axios(config);
  };

  statusCanceled = (quotationId: string) => {
    const config: IRequestConfig<string> = {
      url: `${this.mafraq_url}/Quotation/StatusCanceled/${quotationId}`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.get
    };

    return axios(config);
  };


  allBudgetsSavedInQuote = (quotationId: string) => {
    const config: IRequestConfig<string> = {
      url: `${this.mafraq_url}/Quotation/AllBudgetsSavedInQuote/${quotationId}`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.get
    };

    return axios(config);
  };

  changeIsValidBudget = (quoteId: string, sequenceNumber: number, sequenceBudget?: number) => {
    const config: IRequestConfig<string> = {
      url: `${this.mafraq_url}/Budget/ChangeIsValidBudget`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.put,
      data: {
        quoteId,
        sequenceNumber,
        sequenceBudget
      }
    };

    return axios(config);
  };

  getMinimumBillingDeliveryDays = (data: IMinimumBilling) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Quotation/GetMinimumBillingDeliveryDays`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      params: data,
      method: MethodsTypes.get
    };

    return axios(config);
  };

  resetMultipleBugets = (quoteId: string, sequenceNumbers: number[]) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Budget/ResetMultipleBudgets`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.post,
      data: {
        quoteId,
        sequenceNumbers
      }
    };

    return axios(config);
  };

  deleteSentItem = (quotationId: string, sequenceNumber: number) => {
    let config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Quotation/DeleteSentItem/${quotationId}/${sequenceNumber}`,
      method: MethodsTypes.delete,
      headers: {
        Authorization: `Bearer ${this.access_token()}`,
      },
    };

    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return error.response;
      }
    );
    return axios(config).then((result) => result);
  };

  deleteBudget = (quoteId: string, sequenceNumber: number, sequenceBudget: number, name: string, email: string, distributionCenter: string, notes: string, supplierProductCode: string) => {
    let config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Budget/DeleteBudget`,
      headers: {
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.put,
      data: {
        quoteId,
        sequenceNumber,
        sequenceBudget,
        name,
        email,
        distributionCenter,
        notes,
        supplierProductCode,
      }
    };

    return axios(config);
  };

  getQuoteSentHeaderTabs = (quoteId: string) => {
    let config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Quotation/QuoteSentHeaderTabs/${quoteId}`,
      method: MethodsTypes.get,
      headers: {
        Authorization: `Bearer ${this.access_token()}`,
      },
    };
    return axios(config);
  }; 

  getWGGByCode = (code: string) => {
    const config: IRequestConfig<any> = {
      url: `${this.mafraq_url}/Quotation/GetWGGByCode/${code}`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.access_token()}`,
      },
      method: MethodsTypes.get
    };

    return axios(config);
  };
}
